import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';

import {
	validate_name
} from "../tools";

import {
	update_scan_token,
	get_bearers,
	get_scan_tokens
} from "../tools-network";

import { showTemporaryMessage } from "../components/Message";
import ScanTokens from "../components/ScanTokens";

const ScanTokenNameChange = ({loginToken, userId, scanToken, updateListOfScanTokens, reportEditingState, isOtherEditing}) => {
	const { t } = useTranslation();

	// View the components that actually update the name
	const [enableNameChange, setEnableNameChange]		= useState(false);

	// Only enable the submit button whenever the new name is not equal to the current name
	const [submitButtonEnabled, setSubmitButtonEnabled]	= useState(false);
	const [newName, setNewName]							= useState("");
	const [existingWristbands, setExistingWristbands]	= useState([]);
	const [nameWarning, setNameWarning]					= useState('');

	// Make components inactive and show progress bar when update is in progress
	const [updateInProgress, setUpdateInProgress]		= useState(false);

	useEffect(() => {
		if (reportEditingState) {
			reportEditingState(enableNameChange);
		}
	}, [enableNameChange]);

	useEffect(() => {
		setNewName(scanToken?.name ?? "");
	}, [scanToken]);

	// Fetch all user wristbands for duplicate checking
	useEffect(() => {
		const fetchAllUserWristbands = async () => {
			if (!userId) return;
			
			try {
				const bearersRes = await get_bearers(loginToken, userId);
				if (bearersRes.status && bearersRes.data) {
					const bearerIds = Object.keys(bearersRes.data);
					
					const allWristbandPromises = bearerIds.map(bearerId => 
						get_scan_tokens(loginToken, bearerId)
					);
					const allWristbandsResults = await Promise.all(allWristbandPromises);
					
					const allNames = [];
					allWristbandsResults.forEach(res => {
						if (res.status && res.data) {
							res.data.forEach(token => {
								if (token.name && token.token !== scanToken?.token) {
									allNames.push(token.name);
								}
							});
						}
					});
					
					setExistingWristbands(allNames);
				}
			} catch (err) {
				console.log("Error fetching user's wristbands: " + err);
			}
		};
		
		fetchAllUserWristbands();
	}, [loginToken, userId, scanToken]);

	// Check for duplicate names
	useEffect(() => {
		if (newName.trim() && newName !== scanToken?.name && existingWristbands.length > 0) {
			const isDuplicate = existingWristbands.some(
				existingName => existingName.toLowerCase() === newName.trim().toLowerCase()
			);
			if (isDuplicate) {
				setNameWarning(t("wristband_name_already_exists"));
			} else {
				setNameWarning('');
			}
		} else {
			setNameWarning('');
		}
	}, [newName, existingWristbands, scanToken, t]);

	useEffect(() => {
		const isEnabled = newName != scanToken?.name && !nameWarning;
		setSubmitButtonEnabled(isEnabled);
	}, [newName, nameWarning, scanToken]);

	async function submitUpdate() {
		if(!validate_name(newName)) {
			showTemporaryMessage("ERROR", "Name is not valid!", 'danger');
			return;
		}
		if (nameWarning) {
			showTemporaryMessage(t("wristband_name_already_exists"), t("please_choose_different_name"), 'warning');
			return;
		}
		setUpdateInProgress(true);
		const updateFields = { name: newName };
		setNewName("");
		try {
			const res = await update_scan_token(loginToken, scanToken.token, updateFields);
			if(res.success) {
				showTemporaryMessage("Scan token information successfully updated!", " ", 'success');
				setUpdateInProgress(false);
				updateListOfScanTokens();
				setEnableNameChange(false);
			} else {
				// Handle 409 conflict error
				if (res.msg && res.msg.includes('Name already exists')) {
					showTemporaryMessage(t("wristband_name_already_exists"), t("please_choose_different_name"), 'warning');
				} else {
					showTemporaryMessage("Update scan token FAILED!", res.msg, 'danger');
				}
				setUpdateInProgress(false);
				setEnableNameChange(false);
			}
		} catch (err) {
			showTemporaryMessage("Update scan token FAILED!", err, 'danger');
			setUpdateInProgress(false);
			setEnableNameChange(false);
		}
	};

	return (
		<>
			{scanToken ? (
				<>
					{/* ENABLE NAME UPDATE DIV */}
					{!enableNameChange && !isOtherEditing && (
						<button
							onClick={() => setEnableNameChange(true)}
							className="btn-secondary text-white px-4 py-2 rounded-md text-sm font-semibold transition ease-in-out hover:scale-105 disabled:bg-gray-400 disabled:cursor-not-allowed"
						>
							{t("update_name_of_scan_token")}
						</button>
					)}

											{/* ACTUAL NAME UPDATE DIV */}
										{enableNameChange && (
											<div className="bg-white rounded-lg p-6 shadow-sm">
												<h4 className="text-xl font-bold text-gray-900 mb-4">{t("update_name_of_scan_token")}</h4>
												<div className="flex flex-col items-start w-full space-y-3">
												<label htmlFor="update-name-of-scan-token-div" className="text-sm font-medium text-gray-700">
													{t("current_name")}:
												</label>
												<input
													id="update-name-of-scan-token-div"
													value={newName}
													disabled={updateInProgress}
													onChange={(e) => {
														setNewName(e.target.value);}}
													className={`block w-full rounded-md border px-3 py-2 text-sm ${
														nameWarning ? 'border-yellow-500' : 'border-gray-300'
													}`}
												/>
												{nameWarning && (
													<p className="text-xs text-yellow-600">
														{nameWarning}
													</p>
												)}
												<div className="flex gap-2 mt-4">
													<button onClick={() =>
														{ submitUpdate(); }}
														id='submit-update-name-of-scan-token-div'
														disabled={!submitButtonEnabled || updateInProgress}
													className="btn-secondary px-4 py-2 text-white rounded-md text-sm font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed transition ease-in-out hover:-translate-y-0.5 hover:scale-105">
														{t("submit_change")}
												</button>
												{/* CANCEL BUTTON */}
												<button onClick={() =>
													{ setNewName("");
													setEnableNameChange(false); }}
													className="px-4 py-2 text-red-700 rounded-md font-semibold text-sm disabled:bg-gray-400 disabled:cursor-not-allowed hover:text-black"
														disabled={updateInProgress}>
															{t("cancel")}
													</button>
												</div>
												{updateInProgress && (
													<div id='progress-bar' className="mt-2">
														<progress />
													</div>
												)}
											</div>
										</div>
										)}
				</>
			) : null}
		</>
	);
};
export default ScanTokenNameChange;

